import type {
    ContributionCaptureStore,
    StoredContributionCheckpoint,
    StoredContributionEpisode,
    StoredContributionEvent,
} from "./store";
import type {
    ContributionHttpClient,
    NormalizedContributionCaptureConfigV1,
} from "./types";
import { isContributionCaptureAllowedForProject } from "./config";

const EPISODES_ROUTE = "/instrumentation/episodes";
const MAX_EPISODES_PER_FLUSH = 4;
// Keep headroom below the backend's 2 MiB event and 8 MiB checkpoint limits
// for headers, JSON framing, and future envelope fields.
export const CONTRIBUTION_EVENT_REQUEST_MAX_BYTES = 1_750_000;
export const CONTRIBUTION_CHECKPOINT_REQUEST_MAX_BYTES = 7_000_000;

export interface ContributionFlushSummary {
    status: "success" | "partial" | "skipped" | "failed";
    sentEvents: number;
    sentCheckpoints: number;
    completedEpisodes: number;
    queueDepth: number;
    quarantinedItemCount: number;
    errorCode?: string;
}

export interface ContributionAbandonFlushSummary {
    abandonedEpisodes: number;
    pendingEpisodes: number;
    errorCode?: string;
}

interface TransportRuntime {
    http: ContributionHttpClient | null;
    getToken: (() => string | null | Promise<string | null>) | null;
    ownerUid: string | null;
}

const unique = (values: string[]): string[] => Array.from(new Set(values.filter(Boolean)));

const stringId = (value: unknown): string => {
    if (typeof value === "string") return value.trim();
    if (!value || typeof value !== "object") return "";
    const row = value as Record<string, unknown>;
    return String(
        row.eventId ?? row.event_id ?? row.checkpointId ?? row.checkpoint_id ?? row.id ?? "",
    ).trim();
};

const arrayAt = (value: Record<string, unknown>, ...keys: string[]): unknown[] => {
    for (const key of keys) {
        if (Array.isArray(value[key])) return value[key] as unknown[];
    }
    return [];
};

interface AckSet {
    accepted: string[];
    duplicate: string[];
    rejected: string[];
    rejectionReasons: Record<string, string>;
    retryable: string[];
    hasExplicitAckFields: boolean;
}

function normalizeAckResponse(data: unknown, kind: "event" | "checkpoint"): AckSet {
    const value = data && typeof data === "object" ? data as Record<string, unknown> : {};
    const prefix = kind === "event" ? "Event" : "Checkpoint";
    const snakePrefix = kind === "event" ? "event" : "checkpoint";
    const acceptedKeys = [`accepted${prefix}Ids`, `accepted_${snakePrefix}_ids`];
    const duplicateKeys = [`duplicate${prefix}Ids`, `duplicate_${snakePrefix}_ids`];
    const retryableKeys = [`retryable${prefix}Ids`, `retryable_${snakePrefix}_ids`];
    const rejectedKeys = kind === "event"
        ? ["rejected", "rejectedEventIds", "rejected_event_ids"]
        : ["rejectedCheckpoints", "rejectedCheckpointIds", "rejected_checkpoint_ids", "rejected"];
    const allKeys = [...acceptedKeys, ...duplicateKeys, ...retryableKeys, ...rejectedKeys];
    const rawRejected = arrayAt(value, ...rejectedKeys);
    const rejectionReasons: Record<string, string> = {};
    for (const item of rawRejected) {
        const id = stringId(item);
        if (!id) continue;
        const row = item && typeof item === "object" ? item as Record<string, unknown> : {};
        rejectionReasons[id] = String(
            row.reasonCode ?? row.reason_code ?? row.code ?? row.reason ?? "rejected",
        ).slice(0, 120);
    }
    return {
        accepted: unique(arrayAt(value, ...acceptedKeys).map(stringId)),
        duplicate: unique(arrayAt(value, ...duplicateKeys).map(stringId)),
        rejected: unique(rawRejected.map(stringId)),
        rejectionReasons,
        retryable: unique(arrayAt(value, ...retryableKeys).map(stringId)),
        hasExplicitAckFields: allKeys.some((key) => Object.prototype.hasOwnProperty.call(value, key)),
    };
}

const episodeVersionFromResponse = (data: unknown): string => {
    if (!data || typeof data !== "object") return "";
    const value = data as Record<string, unknown>;
    return String(
        value.episodeVersion ?? value.episode_version ?? value.version ?? value.id ?? "",
    ).trim();
};

const retryDelayMs = (attemptCount: number): number =>
    Math.min(5 * 60_000, 1_000 * Math.pow(2, Math.max(0, Math.min(8, attemptCount))));

const errorCodeFrom = (error: unknown): string => {
    const status = Number((error as any)?.response?.status);
    if (Number.isFinite(status) && status > 0) return `http_${status}`;
    return "network_error";
};

const httpStatusFrom = (error: unknown): number | null => {
    const status = Number((error as any)?.response?.status);
    return Number.isInteger(status) && status > 0 ? status : null;
};

const isTerminalClientStatus = (status: number | null): boolean =>
    status !== null && status >= 400 && status < 500 &&
    status !== 408 && status !== 425 && status !== 429;

const tokenOwnerClaims = (token: string): string[] => {
    const payloadSegment = token.split(".")[1];
    if (!payloadSegment || typeof atob !== "function") return [];
    try {
        const base64 = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
        const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
        const payload = JSON.parse(atob(padded)) as Record<string, unknown>;
        return unique([payload.sub, payload.user_id]
            .filter((value): value is string => typeof value === "string")
            .map((value) => value.trim()));
    } catch {
        return [];
    }
};

const utf8ByteLength = (value: string): number => {
    let bytes = 0;
    for (const character of value) {
        const point = character.codePointAt(0) ?? 0;
        bytes += point <= 0x7f ? 1 : point <= 0x7ff ? 2 : point <= 0xffff ? 3 : 4;
    }
    return bytes;
};

interface RequestBudgetSelection<T> {
    rows: T[];
    oversizedRows: T[];
}

const rowsWithinRequestBudget = <T, V>(args: {
    rows: T[];
    arrayKey: "events" | "checkpoints";
    value: (row: T) => V;
    maxBytes: number;
}): RequestBudgetSelection<T> => {
    const emptyBody = JSON.stringify({ schemaVersion: 1, [args.arrayKey]: [] });
    const emptyBodyBytes = utf8ByteLength(emptyBody);
    let usedBytes = emptyBodyBytes;
    const selected: T[] = [];
    const oversizedRows: T[] = [];
    for (const row of args.rows) {
        const encoded = JSON.stringify(args.value(row));
        const encodedBytes = utf8ByteLength(encoded);
        if (emptyBodyBytes + encodedBytes > args.maxBytes) {
            oversizedRows.push(row);
            continue;
        }
        const itemBytes = encodedBytes + (selected.length > 0 ? 1 : 0);
        if (usedBytes + itemBytes > args.maxBytes) break;
        selected.push(row);
        usedBytes += itemBytes;
    }
    return { rows: selected, oversizedRows };
};

export class ContributionBatchTransport {
    private runtime: TransportRuntime = { http: null, getToken: null, ownerUid: null };
    private runtimeGeneration = 0;

    constructor(private readonly store: ContributionCaptureStore) {}

    configure(runtime: TransportRuntime): void {
        this.runtime = runtime;
        this.runtimeGeneration += 1;
    }

    invalidate(): void {
        this.runtimeGeneration += 1;
    }

    async flushAbandonments(): Promise<ContributionAbandonFlushSummary> {
        const runtime = this.runtime;
        const runtimeGeneration = this.runtimeGeneration;
        const runtimeIsCurrent = () =>
            this.runtime === runtime && this.runtimeGeneration === runtimeGeneration;
        const ownerUid = String(runtime.ownerUid ?? "").trim();
        if (!ownerUid) return { abandonedEpisodes: 0, pendingEpisodes: 0, errorCode: "missing_owner" };
        const episodes = await this.store.listAbandoningEpisodes(ownerUid, 100);
        if (!runtimeIsCurrent()) {
            return { abandonedEpisodes: 0, pendingEpisodes: episodes.length, errorCode: "runtime_changed" };
        }
        if (episodes.length === 0) {
            return { abandonedEpisodes: 0, pendingEpisodes: 0 };
        }
        if (typeof navigator !== "undefined" && navigator.onLine === false) {
            return { abandonedEpisodes: 0, pendingEpisodes: episodes.length, errorCode: "offline" };
        }
        const http = runtime.http;
        const getToken = runtime.getToken;
        if (!http || !getToken) {
            return { abandonedEpisodes: 0, pendingEpisodes: episodes.length, errorCode: "transport_unconfigured" };
        }
        const token = await getToken();
        if (!runtimeIsCurrent()) {
            return { abandonedEpisodes: 0, pendingEpisodes: 0, errorCode: "runtime_changed" };
        }
        if (!token) return { abandonedEpisodes: 0, pendingEpisodes: episodes.length, errorCode: "missing_token" };
        const claimedOwners = tokenOwnerClaims(token);
        if (claimedOwners.length > 0 && claimedOwners.some((claim) => claim !== ownerUid)) {
            return { abandonedEpisodes: 0, pendingEpisodes: episodes.length, errorCode: "token_owner_mismatch" };
        }
        let abandonedEpisodes = 0;
        let terminalEpisodes = 0;
        let errorCode: string | undefined;
        const now = Date.now();
        for (const episode of episodes) {
            if (!runtimeIsCurrent()) {
                return { abandonedEpisodes, pendingEpisodes: episodes.length - abandonedEpisodes, errorCode: "runtime_changed" };
            }
            if (episode.nextAttemptAtMs > now) continue;
            if (!episode.abandonPayload) {
                await this.store.markEpisodeBlocked(ownerUid, episode.clientEpisodeId, "missing_abandon_payload");
                terminalEpisodes += 1;
                errorCode = "missing_abandon_payload";
                continue;
            }
            const version = String(episode.serverEpisodeVersion ?? episode.clientEpisodeId).trim();
            try {
                await http.post(
                    `${EPISODES_ROUTE}/${encodeURIComponent(version)}/abandon`,
                    episode.abandonPayload,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Idempotency-Key": `${episode.clientEpisodeId}:abandon`,
                        },
                    },
                );
                if (!runtimeIsCurrent()) {
                    return { abandonedEpisodes, pendingEpisodes: episodes.length - abandonedEpisodes, errorCode: "runtime_changed" };
                }
                await this.store.markEpisodeAbandoned(ownerUid, episode.clientEpisodeId);
                abandonedEpisodes += 1;
            } catch (error) {
                if (!runtimeIsCurrent()) {
                    return { abandonedEpisodes, pendingEpisodes: episodes.length - abandonedEpisodes, errorCode: "runtime_changed" };
                }
                const code = errorCodeFrom(error);
                errorCode = code;
                const status = httpStatusFrom(error);
                // A token can expire between retrieval and request dispatch. Keep
                // the owner-bound control record retryable on 401; 403 remains a
                // permanent ownership/authorization failure.
                if (isTerminalClientStatus(status) && status !== 401) {
                    await this.store.markEpisodeBlocked(ownerUid, episode.clientEpisodeId, code);
                    terminalEpisodes += 1;
                } else {
                    await this.store.markEpisodeRetry(
                        ownerUid,
                        episode.clientEpisodeId,
                        now + retryDelayMs(episode.attemptCount),
                    );
                }
            }
        }
        const pendingEpisodes = Math.max(0, episodes.length - abandonedEpisodes - terminalEpisodes);
        return {
            abandonedEpisodes,
            pendingEpisodes,
            ...(errorCode
                ? { errorCode }
                : pendingEpisodes > 0 ? { errorCode: "abandonment_pending" } : {}),
        };
    }

    async flush(config: NormalizedContributionCaptureConfigV1): Promise<ContributionFlushSummary> {
        // Bind every async step in this flush to one authentication/configuration
        // generation. In particular, never pair an old owner's queue with a token
        // returned after an account switch.
        const runtime = this.runtime;
        const runtimeGeneration = this.runtimeGeneration;
        const runtimeIsCurrent = () =>
            this.runtime === runtime && this.runtimeGeneration === runtimeGeneration;
        const ownerUid = String(runtime.ownerUid ?? "").trim();
        const [initialQueueDepth, initialQuarantineCount] = await Promise.all([
            this.safeQueueDepth(ownerUid),
            this.safeQuarantineCount(ownerUid),
        ]);
        const base: ContributionFlushSummary = {
            status: "skipped",
            sentEvents: 0,
            sentCheckpoints: 0,
            completedEpisodes: 0,
            queueDepth: initialQueueDepth,
            quarantinedItemCount: initialQuarantineCount,
        };
        const runtimeChanged = (): ContributionFlushSummary => ({
            ...base,
            status: "skipped",
            queueDepth: 0,
            quarantinedItemCount: 0,
            errorCode: "runtime_changed",
        });
        if (!runtimeIsCurrent()) return runtimeChanged();
        if (!config.enabled) return base;
        if (!ownerUid) return { ...base, errorCode: "missing_owner" };
        if (typeof navigator !== "undefined" && navigator.onLine === false) {
            return { ...base, errorCode: "offline" };
        }
        const http = runtime.http;
        const getToken = runtime.getToken;
        if (!http || !getToken) return { ...base, errorCode: "transport_unconfigured" };
        const token = await getToken();
        if (!runtimeIsCurrent()) return runtimeChanged();
        if (!token) return { ...base, errorCode: "missing_token" };
        const claimedOwners = tokenOwnerClaims(token);
        if (claimedOwners.length > 0 && claimedOwners.some((claim) => claim !== ownerUid)) {
            return { ...base, status: "failed", errorCode: "token_owner_mismatch" };
        }

        const headers = { Authorization: `Bearer ${token}` };
        let hadFailure = false;
        let lastErrorCode: string | undefined;

        try {
            const episodes = (await this.store.listOpenEpisodes(ownerUid, 100))
                .filter((episode) =>
                    episode.ownerUid === ownerUid &&
                    episode.createPayload.captureConfigId === config.captureConfigId &&
                    episode.createPayload.consentScopeId === config.consentScopeId &&
                    episode.createPayload.profile === config.profile &&
                    isContributionCaptureAllowedForProject(config, episode.createPayload.projectId)
                )
                .slice(0, MAX_EPISODES_PER_FLUSH);
            if (!runtimeIsCurrent()) return runtimeChanged();
            for (const episode of episodes) {
                if (!runtimeIsCurrent()) return runtimeChanged();
                const outcome = await this.flushEpisode({
                    episode,
                    config,
                    http,
                    headers,
                    ownerUid,
                    runtimeIsCurrent,
                });
                if (!runtimeIsCurrent()) return runtimeChanged();
                base.sentEvents += outcome.sentEvents;
                base.sentCheckpoints += outcome.sentCheckpoints;
                base.completedEpisodes += outcome.completedEpisodes;
                if (outcome.errorCode) {
                    hadFailure = true;
                    lastErrorCode = outcome.errorCode;
                }
            }
        } catch (error) {
            if (!runtimeIsCurrent()) return runtimeChanged();
            hadFailure = true;
            lastErrorCode = errorCodeFrom(error);
        }

        if (!runtimeIsCurrent()) return runtimeChanged();
        base.queueDepth = await this.safeQueueDepth(ownerUid);
        if (!runtimeIsCurrent()) return runtimeChanged();
        base.quarantinedItemCount = await this.safeQuarantineCount(ownerUid);
        if (!runtimeIsCurrent()) return runtimeChanged();
        base.errorCode = lastErrorCode;
        if (hadFailure && (base.sentEvents > 0 || base.sentCheckpoints > 0 || base.completedEpisodes > 0)) {
            base.status = "partial";
        } else if (hadFailure) {
            base.status = "failed";
        } else {
            base.status = "success";
        }
        return base;
    }

    private async flushEpisode(args: {
        episode: StoredContributionEpisode;
        config: NormalizedContributionCaptureConfigV1;
        http: ContributionHttpClient;
        headers: Record<string, string>;
        ownerUid: string;
        runtimeIsCurrent: () => boolean;
    }): Promise<{
        sentEvents: number;
        sentCheckpoints: number;
        completedEpisodes: number;
        errorCode?: string;
    }> {
        const { episode, config, http, headers, ownerUid, runtimeIsCurrent } = args;
        const now = Date.now();
        let version = String(episode.serverEpisodeVersion ?? "").trim();

        if (!runtimeIsCurrent()) {
            return { sentEvents: 0, sentCheckpoints: 0, completedEpisodes: 0, errorCode: "runtime_changed" };
        }

        if (!version) {
            if (episode.nextAttemptAtMs > now) {
                return { sentEvents: 0, sentCheckpoints: 0, completedEpisodes: 0 };
            }
            try {
                if (!runtimeIsCurrent()) {
                    return { sentEvents: 0, sentCheckpoints: 0, completedEpisodes: 0, errorCode: "runtime_changed" };
                }
                const response = await http.post(EPISODES_ROUTE, episode.createPayload, {
                    headers: {
                        ...headers,
                        "Idempotency-Key": episode.clientEpisodeId,
                    },
                });
                if (!runtimeIsCurrent()) {
                    return { sentEvents: 0, sentCheckpoints: 0, completedEpisodes: 0, errorCode: "runtime_changed" };
                }
                version = episodeVersionFromResponse(response?.data);
                if (!version) {
                    const code = "missing_episode_version";
                    await this.store.markEpisodeRetry(
                        ownerUid,
                        episode.clientEpisodeId,
                        now + retryDelayMs(episode.attemptCount),
                    );
                    return { sentEvents: 0, sentCheckpoints: 0, completedEpisodes: 0, errorCode: code };
                }
                await this.store.setEpisodeVersion(ownerUid, episode.clientEpisodeId, version);
                if (!runtimeIsCurrent()) {
                    return { sentEvents: 0, sentCheckpoints: 0, completedEpisodes: 0, errorCode: "runtime_changed" };
                }
            } catch (error) {
                if (!runtimeIsCurrent()) {
                    return { sentEvents: 0, sentCheckpoints: 0, completedEpisodes: 0, errorCode: "runtime_changed" };
                }
                const code = errorCodeFrom(error);
                if (isTerminalClientStatus(httpStatusFrom(error))) {
                    await this.store.markEpisodeBlocked(ownerUid, episode.clientEpisodeId, code);
                } else {
                    await this.store.markEpisodeRetry(
                        ownerUid,
                        episode.clientEpisodeId,
                        now + retryDelayMs(episode.attemptCount),
                    );
                }
                return { sentEvents: 0, sentCheckpoints: 0, completedEpisodes: 0, errorCode: code };
            }
        }

        const encodedVersion = encodeURIComponent(version);
        let sentEvents = 0;
        let sentCheckpoints = 0;
        let errorCode: string | undefined;

        const eventRows = await this.store.getReadyEvents(
            ownerUid,
            episode.clientEpisodeId,
            config.maxBatchSize,
            now,
        );
        if (!runtimeIsCurrent()) {
            return { sentEvents, sentCheckpoints, completedEpisodes: 0, errorCode: "runtime_changed" };
        }
        const eventSelection = rowsWithinRequestBudget({
            rows: eventRows,
            arrayKey: "events",
            value: (row) => row.envelope,
            maxBytes: CONTRIBUTION_EVENT_REQUEST_MAX_BYTES,
        });
        if (eventSelection.oversizedRows.length > 0) {
            if (!runtimeIsCurrent()) {
                return { sentEvents, sentCheckpoints, completedEpisodes: 0, errorCode: "runtime_changed" };
            }
            await this.store.quarantineEvents(ownerUid, eventSelection.oversizedRows.map((row) => ({
                id: row.eventId,
                reasonCode: "request_too_large",
            })));
            if (!runtimeIsCurrent()) {
                return { sentEvents, sentCheckpoints, completedEpisodes: 0, errorCode: "runtime_changed" };
            }
            errorCode = "event_request_too_large";
        }
        const eventBatch = eventSelection.rows;
        if (eventBatch.length > 0) {
            const eventResult = await this.postEvents(
                http,
                headers,
                encodedVersion,
                eventBatch,
                ownerUid,
                runtimeIsCurrent,
            );
            sentEvents += eventResult.sent;
            errorCode = errorCode ?? eventResult.errorCode;
            if (eventResult.blocked || eventResult.aborted) {
                return { sentEvents, sentCheckpoints, completedEpisodes: 0, errorCode };
            }
        }

        const checkpointRows = await this.store.getReadyCheckpoints(
            ownerUid,
            episode.clientEpisodeId,
            config.maxBatchSize,
            now,
        );
        if (!runtimeIsCurrent()) {
            return { sentEvents, sentCheckpoints, completedEpisodes: 0, errorCode: "runtime_changed" };
        }
        const checkpointSelection = rowsWithinRequestBudget({
            rows: checkpointRows,
            arrayKey: "checkpoints",
            value: (row) => row.checkpoint,
            maxBytes: CONTRIBUTION_CHECKPOINT_REQUEST_MAX_BYTES,
        });
        if (checkpointSelection.oversizedRows.length > 0) {
            if (!runtimeIsCurrent()) {
                return { sentEvents, sentCheckpoints, completedEpisodes: 0, errorCode: "runtime_changed" };
            }
            await this.store.quarantineCheckpoints(ownerUid, checkpointSelection.oversizedRows.map((row) => ({
                id: row.checkpointId,
                reasonCode: "request_too_large",
            })));
            if (!runtimeIsCurrent()) {
                return { sentEvents, sentCheckpoints, completedEpisodes: 0, errorCode: "runtime_changed" };
            }
            errorCode = errorCode ?? "checkpoint_request_too_large";
        }
        const checkpointBatch = checkpointSelection.rows;
        if (checkpointBatch.length > 0) {
            const checkpointResult = await this.postCheckpoints(
                http,
                headers,
                encodedVersion,
                checkpointBatch,
                ownerUid,
                runtimeIsCurrent,
            );
            sentCheckpoints += checkpointResult.sent;
            errorCode = errorCode ?? checkpointResult.errorCode;
            if (checkpointResult.blocked || checkpointResult.aborted) {
                return { sentEvents, sentCheckpoints, completedEpisodes: 0, errorCode };
            }
        }

        let completedEpisodes = 0;
        const pending = await this.store.getPendingCounts(ownerUid, episode.clientEpisodeId);
        if (!runtimeIsCurrent()) {
            return { sentEvents, sentCheckpoints, completedEpisodes: 0, errorCode: "runtime_changed" };
        }
        if (
            episode.status === "completing" &&
            episode.completePayload &&
            pending.events === 0 &&
            pending.checkpoints === 0
        ) {
            try {
                if (!runtimeIsCurrent()) {
                    return { sentEvents, sentCheckpoints, completedEpisodes: 0, errorCode: "runtime_changed" };
                }
                await http.post(
                    `${EPISODES_ROUTE}/${encodedVersion}/complete`,
                    episode.completePayload,
                    {
                        headers: {
                            ...headers,
                            "Idempotency-Key": episode.clientEpisodeId,
                        },
                    },
                );
                if (!runtimeIsCurrent()) {
                    return { sentEvents, sentCheckpoints, completedEpisodes: 0, errorCode: "runtime_changed" };
                }
                await this.store.markEpisodeCompleted(ownerUid, episode.clientEpisodeId);
                if (!runtimeIsCurrent()) {
                    return { sentEvents, sentCheckpoints, completedEpisodes: 0, errorCode: "runtime_changed" };
                }
                completedEpisodes = 1;
            } catch (error) {
                if (!runtimeIsCurrent()) {
                    return { sentEvents, sentCheckpoints, completedEpisodes: 0, errorCode: "runtime_changed" };
                }
                const code = errorCodeFrom(error);
                errorCode = errorCode ?? code;
                if (isTerminalClientStatus(httpStatusFrom(error))) {
                    await this.store.markEpisodeBlocked(ownerUid, episode.clientEpisodeId, code);
                } else {
                    await this.store.markEpisodeRetry(
                        ownerUid,
                        episode.clientEpisodeId,
                        now + retryDelayMs(episode.attemptCount),
                    );
                }
            }
        }

        return { sentEvents, sentCheckpoints, completedEpisodes, errorCode };
    }

    private async postEvents(
        http: ContributionHttpClient,
        headers: Record<string, string>,
        encodedVersion: string,
        rows: StoredContributionEvent[],
        ownerUid: string,
        runtimeIsCurrent: () => boolean,
    ): Promise<{ sent: number; errorCode?: string; blocked?: boolean; aborted?: boolean }> {
        const ids = rows.map((row) => row.eventId);
        if (!runtimeIsCurrent()) {
            return { sent: 0, errorCode: "runtime_changed", aborted: true };
        }
        try {
            const response = await http.post(
                `${EPISODES_ROUTE}/${encodedVersion}/events`,
                { schemaVersion: 1, events: rows.map((row) => row.envelope) },
                { headers },
            );
            if (!runtimeIsCurrent()) {
                return { sent: 0, errorCode: "runtime_changed", aborted: true };
            }
            const ack = normalizeAckResponse(response?.data, "event");
            const acknowledged = unique([...ack.accepted, ...ack.duplicate]);
            const terminal = unique([...acknowledged, ...ack.rejected]);
            const explicitlyRetryable = new Set(ack.retryable);
            const mentioned = new Set([...terminal, ...ack.retryable]);
            const retryable = unique([
                ...ack.retryable,
                ...ids.filter((id) => !mentioned.has(id)),
            ]);
            await this.store.removeEvents(ownerUid, acknowledged);
            if (!runtimeIsCurrent()) {
                return { sent: 0, errorCode: "runtime_changed", aborted: true };
            }
            await this.store.quarantineEvents(ownerUid, ack.rejected.map((id) => ({
                id,
                reasonCode: ack.rejectionReasons[id] ?? "rejected",
            })));
            if (!runtimeIsCurrent()) {
                return { sent: 0, errorCode: "runtime_changed", aborted: true };
            }
            if (retryable.length > 0) {
                const maxAttempt = Math.max(0, ...rows
                    .filter((row) => retryable.includes(row.eventId))
                    .map((row) => row.attemptCount));
                await this.store.markEventsRetry(
                    ownerUid,
                    retryable,
                    Date.now() + retryDelayMs(maxAttempt),
                    ack.hasExplicitAckFields ? "retryable_or_unacknowledged" : "missing_ack",
                );
                if (!runtimeIsCurrent()) {
                    return { sent: 0, errorCode: "runtime_changed", aborted: true };
                }
            }
            const hasRejection = ack.rejected.length > 0;
            const hasRetry = explicitlyRetryable.size > 0 || retryable.length > 0;
            return {
                sent: ack.accepted.length + ack.duplicate.length,
                ...((hasRejection || hasRetry)
                    ? { errorCode: hasRejection ? "event_rejected" : "event_retryable" }
                    : {}),
            };
        } catch (error) {
            if (!runtimeIsCurrent()) {
                return { sent: 0, errorCode: "runtime_changed", aborted: true };
            }
            const code = errorCodeFrom(error);
            if (isTerminalClientStatus(httpStatusFrom(error))) {
                await this.store.markEpisodeBlocked(ownerUid, rows[0].clientEpisodeId, code);
                return { sent: 0, errorCode: code, blocked: true };
            }
            const maxAttempt = Math.max(0, ...rows.map((row) => row.attemptCount));
            await this.store.markEventsRetry(ownerUid, ids, Date.now() + retryDelayMs(maxAttempt), code);
            return { sent: 0, errorCode: code };
        }
    }

    private async postCheckpoints(
        http: ContributionHttpClient,
        headers: Record<string, string>,
        encodedVersion: string,
        rows: StoredContributionCheckpoint[],
        ownerUid: string,
        runtimeIsCurrent: () => boolean,
    ): Promise<{ sent: number; errorCode?: string; blocked?: boolean; aborted?: boolean }> {
        const ids = rows.map((row) => row.checkpointId);
        if (!runtimeIsCurrent()) {
            return { sent: 0, errorCode: "runtime_changed", aborted: true };
        }
        try {
            const response = await http.post(
                `${EPISODES_ROUTE}/${encodedVersion}/checkpoints`,
                { schemaVersion: 1, checkpoints: rows.map((row) => row.checkpoint) },
                { headers },
            );
            if (!runtimeIsCurrent()) {
                return { sent: 0, errorCode: "runtime_changed", aborted: true };
            }
            const ack = normalizeAckResponse(response?.data, "checkpoint");
            const acknowledged = unique([...ack.accepted, ...ack.duplicate]);
            const terminal = unique([...acknowledged, ...ack.rejected]);
            const mentioned = new Set([...terminal, ...ack.retryable]);
            const retryable = unique([
                ...ack.retryable,
                ...ids.filter((id) => !mentioned.has(id)),
            ]);
            await this.store.removeCheckpoints(ownerUid, acknowledged);
            if (!runtimeIsCurrent()) {
                return { sent: 0, errorCode: "runtime_changed", aborted: true };
            }
            await this.store.quarantineCheckpoints(ownerUid, ack.rejected.map((id) => ({
                id,
                reasonCode: ack.rejectionReasons[id] ?? "rejected",
            })));
            if (!runtimeIsCurrent()) {
                return { sent: 0, errorCode: "runtime_changed", aborted: true };
            }
            if (retryable.length > 0) {
                const maxAttempt = Math.max(0, ...rows
                    .filter((row) => retryable.includes(row.checkpointId))
                    .map((row) => row.attemptCount));
                await this.store.markCheckpointsRetry(
                    ownerUid,
                    retryable,
                    Date.now() + retryDelayMs(maxAttempt),
                    "retryable_or_unacknowledged",
                );
                if (!runtimeIsCurrent()) {
                    return { sent: 0, errorCode: "runtime_changed", aborted: true };
                }
            }
            return {
                sent: ack.accepted.length + ack.duplicate.length,
                ...((ack.rejected.length > 0 || retryable.length > 0)
                    ? { errorCode: ack.rejected.length > 0 ? "checkpoint_rejected" : "checkpoint_retryable" }
                    : {}),
            };
        } catch (error) {
            if (!runtimeIsCurrent()) {
                return { sent: 0, errorCode: "runtime_changed", aborted: true };
            }
            const code = errorCodeFrom(error);
            if (isTerminalClientStatus(httpStatusFrom(error))) {
                await this.store.markEpisodeBlocked(ownerUid, rows[0].clientEpisodeId, code);
                return { sent: 0, errorCode: code, blocked: true };
            }
            const maxAttempt = Math.max(0, ...rows.map((row) => row.attemptCount));
            await this.store.markCheckpointsRetry(ownerUid, ids, Date.now() + retryDelayMs(maxAttempt), code);
            return { sent: 0, errorCode: code };
        }
    }

    private async safeQueueDepth(ownerUid: string): Promise<number> {
        if (!ownerUid) return 0;
        try {
            return await this.store.getQueueDepth(ownerUid);
        } catch {
            return 0;
        }
    }

    private async safeQuarantineCount(ownerUid: string): Promise<number> {
        if (!ownerUid) return 0;
        try {
            return await this.store.getQuarantineCount(ownerUid);
        } catch {
            return 0;
        }
    }
}

export const contributionTransportRoutes = {
    episodes: EPISODES_ROUTE,
    events: (episodeVersion: string) => `${EPISODES_ROUTE}/${encodeURIComponent(episodeVersion)}/events`,
    checkpoints: (episodeVersion: string) => `${EPISODES_ROUTE}/${encodeURIComponent(episodeVersion)}/checkpoints`,
    complete: (episodeVersion: string) => `${EPISODES_ROUTE}/${encodeURIComponent(episodeVersion)}/complete`,
} as const;
